import moment from 'moment';

import eclipses      from 'app/tyler/components/moon/data/eclipses';
import allPhases     from 'app/tyler/components/moon/data/phases';
import allSignsTrop  from 'app/tyler/components/moon/data/signs-tropical';
import allSignsSide1 from 'app/tyler/components/moon/data/signs-lahiri';
import allSignsSide2 from 'app/tyler/components/moon/data/signs-fagan-bradley';

let _signType = 'sidereal1';
const setSignType = (signType) => {
  _signType = signType;
};
const getSignType = () => _signType;
const getAllSigns = (st=_signType) => {
  if (st === 'tropical')  return allSignsTrop;
  if (st === 'sidereal1') return allSignsSide1;
  if (st === 'sidereal2') return allSignsSide2;
  return allSignsSide1;
};

const getYearTimestamps = (year) => {
  const m = moment.utc(`${year}-01-01`);
  const start = m.clone().startOf('year').unix();
  const end = m.clone().endOf('year').unix();
  const dur = end - start;
  return {start, end, dur};
};

const getSignIndexOfTs = (ts, st=_signType) => {
  const allSigns = getAllSigns(st);
  let lower = 0;
  let upper = allSigns.length - 1;
  while (lower <  upper) {
    const mid = Math.round((upper + lower) / 2);
    const s = allSigns[mid];
    if (s[0] > ts) {
      upper = mid - 1;
    } else if ((s[1] - 1) < ts) {
      lower = mid;
    } else {
      return mid;
    }
  }
  return lower;
};

const getPhaseIndexOfTs = (ts) => {
  let lower = 0;
  let upper = allPhases.length - 1;
  while (lower <  upper) {
    const mid = Math.round((upper + lower) / 2);
    const s = allPhases[mid];
    if (s[0] > ts) {
      upper = mid - 1;
    } else if ((s[1] - 1) < ts) {
      lower = mid;
    } else {
      return mid;
    }
  }
  return lower;
};

const getSignOfTs = (ts, st=_signType) => {
  const index = getSignIndexOfTs(ts, st);
  const allSigns = getAllSigns(st);
  return allSigns[index];
};

const getPhaseOfTs = (ts) => {
  const index = getPhaseIndexOfTs(ts);
  return allPhases[index];
};

// provides the index of the first eclipse that occurs after the given timestamp
const getEclipseIndexOfTs = (ts) => {
  if (eclipses[0][0] > ts) return 0;
  if (eclipses[eclipses.length - 1][0] < ts) return null;

  let lower = 0;
  let upper = eclipses.length - 1;
  while (lower < upper) {
    const mid = Math.round((upper + lower) / 2);
    const midTs = eclipses[mid][0];
    if (midTs > ts) {
      upper = mid - 1;
    } else if (midTs < ts) {
      lower = mid;
    } else {
      return mid;
    }
  }
  return (eclipses[lower][0] < ts) ? (lower + 1) : lower;
};

const getSigns = (startTs, endTs, st=_signType) => {
  const allSigns = getAllSigns(st);
  const startI = getSignIndexOfTs(startTs, st);
  const endI = getSignIndexOfTs(endTs, st);
  const s = [];
  for (let i = startI; i <= endI; i++) {
    s.push([...allSigns[i], i]);
  }
  return s;
};

const getPhases = (startTs, endTs) => {
  const startI = getPhaseIndexOfTs(startTs);
  const endI = getPhaseIndexOfTs(endTs);
  const s = [];
  for (let i = startI; i <= endI; i++) {
    s.push([...allPhases[i], i]);
  }
  return s;
};

const getEclipses = (startTs, endTs) => {
  const firstIndex = getEclipseIndexOfTs(startTs);
  let i = firstIndex;
  const e = [];
  while (eclipses[i]?.[0] && (eclipses[i][0] <= endTs)) {
    e.push([...eclipses[i], i]);
    i += 1;
  }
  return e;
};

const getYearSigns = (year, st=_signType) => {
  const yearTs = getYearTimestamps(year);
  const signs = getSigns(yearTs.start, yearTs.end, st);
  return signs.map((sign) => {
    return {
      startTs: sign[0],
      endTs: sign[1] - 1,
      name: sign[2],
    };
  });
};

const getYearPhases = (year) => {
  const yearTs = getYearTimestamps(year);
  const phases = getPhases(yearTs.start, yearTs.end);
  return phases.map((phase) => {
    return {
      startTs: phase[0],
      endTs: phase[1] - 1,
      peakTs: phase[2],
      name: phase[3],
    };
  });
};

const getYearEclipses = (year) => {
  const yearTs = getYearTimestamps(year);
  const eclipses = getEclipses(yearTs.start, yearTs.end);

  return eclipses.map((eclipse) => {
    return {
      ts: eclipse[0],
      name: eclipse[1],
    };
  });
};

const getYearMonths = (year) => {
  const m = moment.utc(`${year}-01-01`).startOf('year');
  const p = [];
  while (m.get('year') === year) {
    const startTs = m.clone().startOf('month').unix();
    const endTs = m.clone().add(1, 'month').startOf('month').unix();
    const dur = endTs - startTs;
    p.push({
      name: m.format('MMMM'),
      isoNum: m.format('MM'),
      year,
      startTs,
      endTs,
    });
    m.add(1, 'month');
  }
  return p;
};

const getYearDays = (year) => {
  const m = moment.utc(`${year}-01-01`).startOf('year');
  const p = [];
  while (m.get('year') === year) {
    const startTs = m.clone().startOf('day').unix();
    const endTs = m.clone().add(1, 'day').startOf('day').unix();
    p.push({
      num: m.format('D'),
      isoNum: m.format('DD'),
      isoDate: m.format('YYYY-MM-DD'),
      startTs,
      endTs,
    });
    m.add(1, 'day');
  }
  return p;
};

export default {
  setSignType,
  getSignType,
  getAllSigns,
  allPhases,
  eclipses,
  getYearTimestamps,
  getSignIndexOfTs,
  getEclipseIndexOfTs,
  getSigns,
  getYearSigns,
  getYearEclipses,
  getYearPhases,
  getYearMonths,
  getYearDays,
  getSignOfTs,
  getPhaseOfTs,
};
